import { createContext, useContext, useMemo, type ReactNode } from "react";
import { appConfig } from "@/services/config";
import {
  buildOidcAuthorizeUrl,
  type AuthConnectivityMode,
  type DemoLoginResult,
  type LoginOptions,
} from "@/services/auth";
import { useSessionContext } from "./SessionProvider";
import { useUserContext } from "./UserContext";

export type AuthContextValue = {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: ReturnType<typeof useUserContext>["user"];
  accessToken: string | null;
  loginWithOidc: () => void;
  loginDev: () => void;
  loginWithDemoAccount: (
    email: string,
    password: string,
    options?: LoginOptions,
  ) => Promise<DemoLoginResult>;
  loginWithPassword: (
    email: string,
    password: string,
    mode: AuthConnectivityMode,
    options?: LoginOptions,
  ) => Promise<DemoLoginResult>;
  logout: () => void;
};

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * AuthProvider composes session + user.
 * Permission checks are NEVER performed here — the backend alone authorizes.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const session = useSessionContext();
  const { user } = useUserContext();

  const value = useMemo<AuthContextValue>(
    () => ({
      isAuthenticated: session.isAuthenticated,
      isLoading: session.isLoading,
      user,
      accessToken: session.session?.accessToken ?? null,
      loginWithOidc: () => {
        const redirectUri = `${window.location.origin}/login`;
        window.location.assign(buildOidcAuthorizeUrl(appConfig.oidcUrl, redirectUri));
      },
      loginDev: () => session.establishDevSession(),
      loginWithDemoAccount: (email, password, options) =>
        session.loginWithDemoAccount(email, password, options),
      loginWithPassword: (email, password, mode, options) =>
        session.loginWithPassword(email, password, mode, options),
      logout: () => session.clear(),
    }),
    [session, user],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuthContext(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuthContext must be used within AuthProvider");
  }
  return ctx;
}
